"use client";

import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RAZOR_PAY_KEY } from "../../common/URLs";
import noData from "./nodata1.png";
import {
  fetchCandidateSubscriptionsAsync,
  selectCandidateSubscriptions,
} from "../candidateSlice/CandidateSubscriptions/CandidateSubscriptionsSlice";
import { paymentGatewayAsync } from "../candidateSlice/PaymentGateway/PaymentGatewaySlice";
import { fetchBasicDetailsBySingleParamAsync, selectBasicDetailsBySingleParam } from "../../profile/BasicDetailsSingleParam/BasicDetailsSingleParamSlice";

const SubscriptionPlan = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingSubscription, setLoadingSubscription] = useState({});

  // Initialize subscriptions as empty array if undefined
  const subscriptions = useSelector(selectCandidateSubscriptions) || [];
  const basicDetails = useSelector(selectBasicDetailsBySingleParam);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasFetchedSubscription = useRef(false)
  const hasFetchedCandidateDetails = useRef(false)

  useEffect(() => {
    if (!hasFetchedSubscription.current) {
      hasFetchedSubscription.current = true;
      dispatch(fetchCandidateSubscriptionsAsync());
    }

    if (!hasFetchedCandidateDetails.current) {
      hasFetchedCandidateDetails.current = true;
      dispatch(fetchBasicDetailsBySingleParamAsync());
    }

    window.scrollTo(0, 0);
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  const renderFeature = (subscription, featureName, value) => {
    return (
      <li className="flex items-center">
        <i
          className={`fa ${value > 0
            ? "fa-check-circle text-green-500"
            : "fa-times-circle text-red-500"
            } mr-3 text-xl`}
        ></i>
        <span className="text-gray-700 text-sm font-medium">
          {featureName} {value > 1 ? `(${value})` : ""}
        </span>
      </li>
    );
  };

  const handlePayment = async (subscription_uuid) => {
    try {
      setLoadingSubscription((prev) => ({
        ...prev,
        [subscription_uuid]: true,
      }));

      const resultAction = await dispatch(
        paymentGatewayAsync({ subscription_uuid })
      );
      console.log("API Response:", resultAction);

      const response = resultAction?.payload?.response;
      if (!response) {
        console.error("Invalid API Response:", resultAction.payload);
        return;
      }

      const { order_id, total_amount, msg, txnid, currency, uuid } = response;

      if (!window.Razorpay) {
        console.error("Razorpay SDK not loaded.");
        return;
      }

      const options = {
        key: RAZOR_PAY_KEY,
        amount: total_amount * 100,
        currency: currency || "INR",
        name: "Indiahirings.com",
        description: msg || "Subscription Payment",
        order_id,
        handler: (paymentResponse) => {
          console.log("Payment Successful:", paymentResponse);
          navigate("/success-page", {
            state: { uuid },
          });
          sendPaymentResponse(paymentResponse);
        },
        prefill: {
          name: basicDetails?.name,
          email: basicDetails?.email,
          contact: `91${basicDetails?.phone}`,
        },
        theme: {
          color: "#3399cc",
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error("Payment Error:", error);
    } finally {
      setLoadingSubscription((prev) => ({
        ...prev,
        [subscription_uuid]: false,
      }));
    }
  };

  const sendPaymentResponse = (response) => {
    console.log("Sending payment response:", response);
  };

  return (
    <div className="">
      <div
        className="p-3 bg-gradient-to-r from-blue-50 to-indigo-200 min-h-screen"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="px-4 py-4 h-full">
          <div className="pb-10">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-gray-800">
                Our Pricing & Plans
              </h2>
              <p className="text-lg text-gray-600 mt-2">
                Choose the best plan for your needs
              </p>
            </div>

            {/* Subscription Cards */}
            <div className="flex flex-wrap justify-center gap-10 mt-6">
              {isLoading
                ? [...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-lg w-80 p-6"
                  >
                    <div className="animate-pulse">
                      <div className="h-14 w-14 bg-gray-300 rounded-full mx-auto"></div>
                      <div className="h-5 bg-gray-300 w-2/3 mx-auto mt-4"></div>
                      <div className="h-4 bg-gray-300 w-1/2 mx-auto mt-2"></div>
                      <div className="h-4 bg-gray-300 w-full mt-4"></div>
                      <div className="h-4 bg-gray-300 w-full mt-2"></div>
                      <div className="h-4 bg-gray-300 w-full mt-2"></div>
                      <div className="h-10 bg-gray-300 w-full mt-4 rounded-md"></div>
                    </div>
                  </div>
                ))
                : Array.isArray(subscriptions) && subscriptions.map((subscription, index) => (
                  <div
                    key={index}
                    className="bg-white border-gray-200 rounded-xl hover:shadow-lg transform transition-all duration-300 hover:-translate-y-2 w-80"
                  >
                    <div className="p-6">
                      <h2 className="text-center text-2xl font-bold text-indigo-600">
                        {subscription.title}
                      </h2>
                      <p className="text-center text-2xl font-semibold text-gray-800 mt-2">
                        ₹{subscription.amount}/month
                      </p>
                      <p className="text-center text-sm text-gray-600 mt-1">
                        Validity: {subscription.validity}{" "}
                        {subscription.validity_type}
                      </p>
                      <div className="mt-4 border-t border-gray-200 pt-4">
                        <ul className="space-y-3">
                          {renderFeature(
                            subscription,
                            "Profile Creation",
                            subscription.profile_creation
                          )}
                          {renderFeature(
                            subscription,
                            "Notifications",
                            subscription.notification_via_email_sms_whatsapp
                          )}
                          {renderFeature(
                            subscription,
                            "Job Apply",
                            subscription.no_of_job_apply
                          )}
                          {renderFeature(
                            subscription,
                            "Resume Builder",
                            subscription.resume_builder
                          )}

                          {renderFeature(
                            subscription,
                            "Candidate Subscribe Count",
                            subscription.is_candidate_subscribe_count
                          )}
                        </ul>
                      </div>
                      {subscription.is_candidate_subscribe_count === 0 ? (
                        <button
                          className="bg-indigo-600 text-white font-semibold rounded-md mt-6 py-2 w-full hover:bg-indigo-700 transition duration-300 flex justify-center items-center"
                          onClick={() => handlePayment(subscription.uuid)}
                          disabled={loadingSubscription[subscription.uuid]}
                        >
                          {loadingSubscription[subscription.uuid] ? (
                            <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-5 h-5 mr-2"></span>
                          ) : (
                            "Buy Now"
                          )}
                        </button>
                      ) : (
                        <button
                          colorScheme="green"
                          className="bg-green-600 text-white font-semibold rounded-md mt-6 py-2 w-full hover:bg-green-700 transition duration-300 flex justify-center items-center"
                        >
                          Already Purchased
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              {(!isLoading && (!Array.isArray(subscriptions) || subscriptions.length === 0)) && (
                <div className="flex flex-col items-center justify-center w-full  ">
                  <img
                    src={noData || "/placeholder.svg"}
                    className={`  object-contain h-[400px] w-[400px]`}
                    alt=""
                  />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    No Subscription Plan Found
                  </h3>
                  <p className="text-gray-600 text-center max-w-md">
                    We couldn't find any Subscription Plan at the moment. Please
                    check back later.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="text-center text-gray-600 mt-8 text-sm">
          <p>© {new Date().getFullYear()} IndiaHirings.com All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlan;